import React, { useMemo, useState } from 'react';

import { formatDateWithOffset } from '@/lib/time';
import { Ship, Anchor, MapPin, Calendar, Clock, Package, ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import Sidebar from '@/components/sidebar';
import { motion } from 'framer-motion';
import ShipTrackingSimple from './ship-position-map';
import { parseISO } from 'date-fns';

interface ShipmentRouteDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  shipmentData?: any;
  shipmentEvents?: any;
}

interface RouteStopData {
  locationName: string;
  unLocationCode: string;
  portId: string;
  isOrigin?: boolean;
  isDestination?: boolean;
  isTransshipment?: boolean;
  etd?: string;
  atd?: string;
  eta?: string;
  ata?: string;
  etdISO?: string;
  atdISO?: string;
  etaISO?: string;
  ataISO?: string;
  firstEtd?: string;
  firstEta?: string;
  vesselName?: string;
  vesselImo?: number;
}

type ProcessedEvent = {
  id: string;
  type:
    | 'departure'
    | 'arrival'
    | 'load'
    | 'discharge'
    | 'gate_in'
    | 'gate_out'
    | 'stuffing'
    | 'stripping'
    | 'empty_pickup'
    | 'empty_dropoff'
    | 'customs'
    | 'equipment'
    | 'shipment'
    | 'transport'
    | 'other';
  location?: string;
  locationCode?: string;
  datetime: string;
  dateTimeISO: string;
  status: 'completed' | 'current' | 'upcoming';
  vessel?: string;
  classification: 'ACT' | 'EST' | 'PLN' | string;
  portId?: string;
  vesselImo?: number;
};

// Component: Shipment Overview Header
const ShipmentOverviewHeader: React.FC<{
  shipmentData: any;
  transitDays: number | null;
}> = ({ shipmentData, transitDays }) => {
  const containerNumber =
    shipmentData?.customFields?.container || shipmentData?.seaReferences?.containers?.[0] || 'N/A';

  const vesselName = shipmentData?.currentVesselName || 'N/A';
  const vesselImo = shipmentData?.currentVesselImo || 'N/A';
  const etd = shipmentData?.atd || shipmentData?.etd;
  const eta = shipmentData?.ata || shipmentData?.eta;

  const carrier =
    shipmentData?.customFields?.carrier || shipmentData?.parties?.find((p: any) => p.role === 'carrier')?.name;
  const formattedCarrier = carrier?.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');

  const trackingUrls: { [key: string]: string } = {
    maersk: `https://www.maersk.com/tracking/${containerNumber}`,
    msc: `https://www.msc.com/en/track-a-shipment`,
    cma_cgm: `https://www.cma-cgm.com/ebusiness/tracking`,
    hapag_lloyd: `https://www.hapag-lloyd.com/en/online-business/track/track-by-container-solution.html`,
    one: `https://ecomm.one-line.com/one-ecom/manage-shipment/cargo-tracking`,
    oocl: `https://www.oocl.com/eng/ourservices/eservices/cargotracking/Pages/cargotracking.aspx`,
    zim: `https://www.zim.com/tools/track-a-shipment`,
    evergreen: `https://ct.shipmentlink.com/servlet/TDB1_CargoTracking.do`,
    cosco: `https://elines.coscoshipping.com/ebusiness/cargotracking`,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-gray-200 rounded-lg p-4 mb-4 relative"
    >
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {shipmentData?.customFields?.internalReference || 'N/A'}
            </h2>
            <p className="text-sm text-gray-600 font-mono mt-1">{containerNumber}</p>
          </div>
        </div>

        <div className="w-12 h-12 flex items-center justify-center absolute top-0 right-6">
          {carrier && (
            <img
              src={`/carriers-images/${formattedCarrier}.png`}
              alt={carrier}
              className="w-10 h-10 object-contain"
              onError={(e: any) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `<span class="text-xs font-bold text-gray-800">${carrier}</span>`;
              }}
            />
          )}
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-900">
            <b>{shipmentData?.originLocation || 'N/A'} </b> {etd ? `(${etd})` : ''}
          </span>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-gray-900">
            <b>{shipmentData?.destinationLocation || 'N/A'} </b> {eta ? `(${eta})` : ''}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Ship className="w-4 h-4" />
          <span className="font-medium">{vesselName}</span>
          <span className="text-gray-400">•</span>
          <span className="font-mono text-xs">IMO: {vesselImo}</span>
        </div>

        {transitDays !== null && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Clock className="w-4 h-4" />
            <span>Transit time: {transitDays} days</span>
          </div>
        )}

        {carrier && (
          <a
            href={trackingUrls[formattedCarrier] || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <span>Verify on {carrier} website</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
        )}
      </div>
    </motion.div>
  );
};

// Component: Route Stop
const RouteStop: React.FC<RouteStopData> = ({
  locationName,
  unLocationCode,
  isOrigin,
  isDestination,
  isTransshipment,
  etd,
  atd,
  eta,
  ata,
  etdISO,
  atdISO,
  etaISO,
  ataISO,
  vesselName,
  firstEtd,
  firstEta,
}) => {
  const getStopColor = () => {
    if (isOrigin) return 'bg-green-100 text-green-700 border-green-300';
    if (isDestination) return 'bg-blue-100 text-blue-700 border-blue-300';
    return 'bg-yellow-100 text-yellow-700 border-yellow-300';
  };

  const getStopLabel = () => {
    if (isOrigin) return 'Origin';
    if (isDestination) return 'Destination';
    return 'Transhipment';
  };

  const getStopIcon = () => {
    if (isOrigin) return <MapPin className="w-4 h-4" />;
    if (isDestination) return <MapPin className="w-4 h-4" />;
    return <ArrowRight className="w-4 h-4" />;
  };

  const differenceInDays = (date1: string, date2: string) => {
    const date1Parsed = parseISO(date1);
    const date2Parsed = parseISO(date2);
    const difference = Math.ceil((date1Parsed.getTime() - date2Parsed.getTime()) / (1000 * 60 * 60 * 24));
    return difference;
  };

  const timeDifferencesETD = useMemo(() => {
    if (atdISO && firstEtd) {
      return differenceInDays(atdISO, firstEtd);
    }
    if (etdISO && firstEtd && etd !== firstEtd) {
      return differenceInDays(etdISO, firstEtd);
    }
    return null;
  }, [etdISO, atdISO, firstEtd]);

  const timeDifferencesETA = useMemo(() => {
    if (ataISO && firstEta) {
      return differenceInDays(ataISO, firstEta);
    }
    if (etaISO && firstEta && etaISO !== firstEta) {
      return differenceInDays(etaISO, firstEta);
    }
    return null;
  }, [etaISO, ataISO, firstEta]);

  return (
    <div className="relative flex items-start gap-3 py-2">
      <div className={`mt-1 p-2 rounded-full ${getStopColor()}`}>{getStopIcon()}</div>

      <div className="flex-1 min-w-0 flex items-start justify-between gap-4">
        <div className="flex-shrink-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="font-semibold text-gray-900">{locationName}</div>
            <div className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${getStopColor()}`}>
              {getStopLabel()}
            </div>
          </div>
          <div className="text-xs text-gray-500 mb-2">{unLocationCode}</div>
          {vesselName && (
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <Ship className="w-3 h-3" />
              <span>{vesselName}</span>
            </div>
          )}
        </div>

        <div className="text-right text-sm space-y-2 flex-shrink-0">
          {(etd || atd) && (
            <div>
              <div className="text-gray-500 text-xs">{atd ? 'ATD:' : 'ETD:'}</div>
              <div className="font-medium text-gray-900">{atd || etd}</div>
              {timeDifferencesETD && <div className="text-xs text-red-600">+{timeDifferencesETD} days</div>}
            </div>
          )}
          {(eta || ata) && (
            <div>
              <div className="text-gray-500 text-xs">{ata ? 'ATA:' : 'ETA:'}</div>
              <div className="font-medium text-gray-900">{ata || eta}</div>
              {timeDifferencesETA && <div className="text-xs text-red-600">+{timeDifferencesETA} days</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Component: Route Timeline
const RouteTimeline: React.FC<{
  routeStops: RouteStopData[];
}> = ({ routeStops }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <MapPin className="w-4 h-4" />
        Route Details
      </h3>

      {routeStops.length === 0 ? (
        <div className="text-center text-gray-500 text-sm py-4">No route information available</div>
      ) : (
        <div className="relative">
          {/* Vertical connecting line */}
          <div className="absolute left-[18px] top-8 bottom-8 w-0.5 bg-gray-200" />

          <div className="space-y-0">
            {routeStops.map((stop, index) => (
              <RouteStop key={`${stop.portId}-${index}`} {...stop} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ShipmentRouteDrawer: React.FC<ShipmentRouteDrawerProps> = ({ isOpen, onClose, shipmentData, shipmentEvents }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['timeline']));

  const now = Date.now();

  const formattedShipmentData = useMemo(() => {
    const originOffset = shipmentData?.timeOffsets?.etd || shipmentData?.timeOffsets?.atd;
    const destinationOffset = shipmentData?.timeOffsets?.eta || shipmentData?.timeOffsets?.ata;
    return {
      ...shipmentData,
      etd: formatDateWithOffset(shipmentData?.etd, originOffset, 'dd/MM HH:mm'),
      atd: formatDateWithOffset(shipmentData?.atd, originOffset, 'dd/MM HH:mm'),
      eta: formatDateWithOffset(shipmentData?.eta, destinationOffset, 'dd/MM HH:mm'),
      ata: formatDateWithOffset(shipmentData?.ata, destinationOffset, 'dd/MM HH:mm'),
    };
  }, [shipmentData]);

  const processedEvents: ProcessedEvent[] = useMemo(() => {
    if (!shipmentEvents || !Array.isArray(shipmentEvents)) return [];

    const mapDCSAType = (e: any): ProcessedEvent['type'] => {
      const typeRaw = (e.eventType || e.type || '').toString().toUpperCase();
      const codeRaw = (e.eventCode || e.code || '').toString().toUpperCase();

      if (typeRaw === 'TRANSPORT') {
        if (codeRaw === 'DEPA') return 'departure';
        if (codeRaw === 'ARRI') return 'arrival';
        return 'transport';
      }
      if (typeRaw === 'EQUIPMENT') {
        if (codeRaw === 'LOAD') return 'load';
        if (codeRaw === 'DISC' || codeRaw === 'DSCH') return 'discharge';
        if (codeRaw === 'IN' || codeRaw === 'GATE_IN') return 'gate_in';
        if (codeRaw === 'OUT' || codeRaw === 'GATE_OUT') return 'gate_out';
        return 'equipment';
      }
      if (typeRaw === 'SHIPMENT') {
        if (codeRaw === 'RECE') return 'shipment';
        if (codeRaw === 'STUF') return 'stuffing';
        if (codeRaw === 'STRP') return 'stripping';
        if (codeRaw === 'PICK') return 'empty_pickup';
        if (codeRaw === 'DROP') return 'empty_dropoff';
        return 'shipment';
      }
      if (typeRaw === 'CUSTOMS' || codeRaw.startsWith('CUS')) {
        return 'customs';
      }
      return 'other';
    };

    const events = shipmentEvents
      .filter((e: any) => e.transportCall?.modeOfTransport !== 'TRUCK' && e.transportCall?.modeOfTransport !== 'RAIL')
      .map((e: any, index: number): ProcessedEvent => {
        const mappedType = mapDCSAType(e);
        const locationName =
          e.locationData?.locationName ||
          e.location?.locationName ||
          e.locationData?.unLocode ||
          e.location?.unLocationCode;
        const locationCode = e.locationData?.unLocode || e.location?.unLocationCode;
        const vesselName = e.vesselData?.vesselName || e.vessel?.vesselName;
        const vesselImo = e.vesselData?.vesselIMONumber || e.vessel?.vesselIMONumber;

        return {
          id: e.id || `event-${index}`,
          type: mappedType,
          location: locationName && locationCode ? `${locationName} (${locationCode})` : locationName || locationCode,
          locationCode,
          datetime: formatDateWithOffset(e.datetime, e.timeOffset, 'dd/MM HH:mm'),
          dateTimeISO: new Date(e.datetime).toISOString(),
          status: 'upcoming',
          vessel: vesselName,
          classification: e.eventClassification || e.classifier || (e.estimated ? 'EST' : 'ACT'),
          portId: e.portId,
          vesselImo: vesselImo ? parseInt(vesselImo) : undefined,
        };
      })
      .sort(
        (a: ProcessedEvent, b: ProcessedEvent) => new Date(a.dateTimeISO).getTime() - new Date(b.dateTimeISO).getTime(),
      );

    let currentMarked = false;
    return events.map((ev) => {
      const t = new Date(ev.dateTimeISO).getTime();
      if (t < now) return { ...ev, status: 'completed' };
      if (!currentMarked) {
        currentMarked = true;
        return { ...ev, status: 'current' };
      }
      return { ...ev, status: 'upcoming' };
    });
  }, [shipmentEvents]);

  // Build route stops
  const routeStops: RouteStopData[] = useMemo(() => {
    if (!processedEvents.length || !formattedShipmentData) return [];

    const stops: RouteStopData[] = [];
    const originId = formattedShipmentData?.originId;
    const destinationId = formattedShipmentData?.destinationId;

    // Group events by port (using unLocationCode as fallback)
    const portGroups = new Map<string, ProcessedEvent[]>();

    processedEvents
      .filter((e) => e.type === 'departure' || e.type === 'arrival')
      .forEach((event) => {
        // Use portId or locationCode as key
        const key = event.portId || event.locationCode || event.location;
        if (!key) return;

        if (!portGroups.has(key)) {
          portGroups.set(key, []);
        }
        portGroups.get(key)!.push(event);
      });

    // Sort port groups by first event datetime
    const sortedPorts = Array.from(portGroups.entries()).sort((a, b) => {
      const aTime = new Date(a[1][0].datetime).getTime();
      const bTime = new Date(b[1][0].datetime).getTime();
      return aTime - bTime;
    });

    sortedPorts.forEach(([key, events]) => {
      const arrivals = events.filter((e) => e.type === 'arrival');
      const departures = events.filter((e) => e.type === 'departure');

      const portId = events[0].portId || key;
      const locationCode = events[0].locationCode || key;
      const isOrigin = portId === originId;
      const isDestination = portId === destinationId;
      const isTransshipment = !isOrigin && !isDestination;

      const locationName = events[0].location?.split('(')[0].trim() || 'Unknown';
      const unLocationCode = events[0].locationCode || '';

      // Get vessel info - prefer departure vessel, fallback to arrival
      const departureVessel = departures[0]?.vessel;
      const vesselName = departureVessel || arrivals[0]?.vessel;
      const vesselImo = departures[0]?.vesselImo || arrivals[0]?.vesselImo;

      const timeChanges = formattedShipmentData.timeChanges || [];
      const timeChange = timeChanges.find((t: any) => t.unLocationCode === locationCode);
      const firstEtd = timeChange?.field === 'etd' ? timeChange?.datetime : null;
      const firstEta = timeChange?.field === 'eta' ? timeChange?.datetime : null;

      stops.push({
        portId,
        locationName,
        unLocationCode,
        isOrigin,
        isDestination,
        isTransshipment,
        etd: departures.findLast((e) => e.classification === 'EST')?.datetime, // Visual date
        atd: departures.findLast((e) => e.classification === 'ACT')?.datetime,
        eta: arrivals.findLast((e) => e.classification === 'EST')?.datetime,
        ata: arrivals.findLast((e) => e.classification === 'ACT')?.datetime,
        etdISO: departures.findLast((e) => e.classification === 'EST')?.dateTimeISO, // Date for calculations
        atdISO: departures.findLast((e) => e.classification === 'ACT')?.dateTimeISO,
        etaISO: arrivals.findLast((e) => e.classification === 'EST')?.dateTimeISO,
        ataISO: arrivals.findLast((e) => e.classification === 'ACT')?.dateTimeISO,
        firstEtd,
        firstEta,
        vesselName,
        vesselImo,
      });
    });

    return stops;
  }, [processedEvents, formattedShipmentData]);

  // Calculate transit time
  const transitDays = useMemo(() => {
    if (!processedEvents.length || !formattedShipmentData) return null;

    const originDeparture = processedEvents.find(
      (e) =>
        e.type === 'departure' &&
        e.portId === formattedShipmentData?.originId &&
        (e.classification === 'ACT' || e.classification === 'EST'),
    );

    const destinationArrival = processedEvents.find(
      (e) =>
        e.type === 'arrival' &&
        e.portId === formattedShipmentData?.destinationId &&
        (e.classification === 'ACT' || e.classification === 'EST'),
    );

    if (!originDeparture || !destinationArrival) return null;

    const days = Math.floor(
      (new Date(destinationArrival?.dateTimeISO).getTime() - new Date(originDeparture?.dateTimeISO).getTime()) /
        (1000 * 60 * 60 * 24),
    );

    return days;
  }, [processedEvents, formattedShipmentData]);

  const containerNumber =
    formattedShipmentData?.customFields?.container || formattedShipmentData?.seaReferences?.containers?.[0] || 'N/A';

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 border-green-300 text-green-800';
      case 'current':
        return 'bg-blue-100 border-blue-300 text-blue-800';
      case 'upcoming':
        return 'bg-gray-100 border-gray-300 text-gray-600';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-600';
    }
  };

  const getEventIcon = (type: string, status: string) => {
    const iconClass =
      status === 'completed'
        ? 'text-green-600'
        : status === 'current'
        ? 'text-blue-600 animate-pulse'
        : 'text-gray-400';

    switch (type) {
      case 'departure':
        return <Ship className={`w-5 h-5 ${iconClass}`} />;
      case 'arrival':
        return <Anchor className={`w-5 h-5 ${iconClass}`} />;
      case 'load':
      case 'discharge':
        return <Package className={`w-5 h-5 ${iconClass}`} />;
      case 'gate_in':
      case 'gate_out':
        return <MapPin className={`w-5 h-5 ${iconClass}`} />;
      case 'customs':
        return <Anchor className={`w-5 h-5 ${iconClass}`} />;
      default:
        return <Package className={`w-5 h-5 ${iconClass}`} />;
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'DELIVERED':
        return 'bg-green-600';
      case 'IN_PORT':
        return 'bg-blue-600';
      case 'DEPARTED':
      case 'LOADING':
        return 'bg-indigo-600';
      case 'TRANSSHIPMENT':
        return 'bg-yellow-600';
      case 'ORDERED':
      case 'BOOKED':
        return 'bg-gray-600';
      default:
        return 'bg-blue-600';
    }
  };

  const formatStatusLabel = (status: string) => {
    return status
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={onClose}
      header={
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold text-gray-900">Shipment Details</h1>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Shipment Overview Header */}
        {formattedShipmentData && (
          <ShipmentOverviewHeader shipmentData={formattedShipmentData} transitDays={transitDays} />
        )}

        {/* Current Status Card - Refactored */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Destination Status
            </h3>
            <span
              className={`${getStatusBadgeColor(
                formattedShipmentData?.status || 'UNKNOWN',
              )} text-white px-3 py-1 rounded-full text-xs font-medium`}
            >
              {formatStatusLabel(formattedShipmentData?.status || 'Unknown')}
            </span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Anchor className="w-4 h-4 text-gray-500" />
              <span className="font-medium">{formattedShipmentData?.destinationLocation || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Clock className="w-3 h-3" />
              {formattedShipmentData?.ata ? (
                <span>
                  ATA:
                  {formattedShipmentData?.ata}
                </span>
              ) : formattedShipmentData?.eta ? (
                <span>
                  ETA:
                  {formattedShipmentData?.eta}
                </span>
              ) : (
                <span>No arrival date available</span>
              )}
            </div>
          </div>
        </motion.div>

        {/* Route Timeline */}
        <RouteTimeline routeStops={routeStops} />

        {/* Ship Tracking Map */}
        {formattedShipmentData && formattedShipmentData?.currentVesselImo && (
          <motion.div>
            <ShipTrackingSimple
              key={formattedShipmentData?.currentVesselImo}
              imo={formattedShipmentData?.currentVesselImo}
            />
          </motion.div>
        )}

        {/* Timeline of Events */}
        <ExpandableSection
          id="timeline"
          title="Journey Timeline"
          icon={<Calendar className="w-4 h-4" />}
          count={processedEvents.length}
          isExpanded={expandedSections.has('timeline')}
          onToggle={() => toggleSection('timeline')}
        >
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

            <div className="space-y-4">
              {processedEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="relative flex items-start gap-4"
                >
                  {/* Icon */}
                  <div
                    className={`relative z-10 p-2 rounded-full border-2 bg-white ${
                      event.status === 'completed'
                        ? 'border-green-300'
                        : event.status === 'current'
                        ? 'border-blue-400'
                        : 'border-gray-300'
                    }`}
                  >
                    {getEventIcon(event.type, event.status)}
                  </div>

                  {/* Content */}
                  <div className={`flex-1 border rounded-lg p-3 ${getStatusColor(event.status)}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold capitalize">{event.type}</span>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              event.classification === 'ACT'
                                ? 'bg-green-200 text-green-800'
                                : 'bg-blue-200 text-blue-800'
                            }`}
                          >
                            {event.classification}
                          </span>
                        </div>
                        <div className="text-sm font-medium mb-2">{event.location}</div>
                        <div className="flex items-center gap-4 text-xs">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {event.datetime}
                          </div>
                          {event.vessel && (
                            <div className="flex items-center gap-1">
                              <Ship className="w-3 h-3" />
                              {event.vessel}
                            </div>
                          )}
                        </div>
                      </div>
                      {event.status === 'current' && (
                        <div className="ml-2">
                          <div className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium animate-pulse">
                            Current
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </ExpandableSection>

        {/* Booking Details */}
        <ExpandableSection
          id="booking"
          title="Booking Details"
          icon={<Package className="w-4 h-4" />}
          isExpanded={expandedSections.has('booking')}
          onToggle={() => toggleSection('booking')}
        >
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Container Number</span>
              <span className="font-mono text-sm font-semibold text-gray-900">{containerNumber}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Booking Number</span>
              <span className="font-mono text-sm font-semibold text-gray-900">
                {formattedShipmentData?.customFields?.bookingNumber || 'N/A'}
              </span>
            </div>
          </div>
        </ExpandableSection>
      </div>
    </Sidebar>
  );
};

// Helper Components
interface ExpandableSectionProps {
  id: string;
  title: string;
  icon?: React.ReactNode;
  count?: number;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const ExpandableSection: React.FC<ExpandableSectionProps> = ({
  title,
  icon,
  count,
  isExpanded,
  onToggle,
  children,
}) => (
  <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
    <button
      className="w-full p-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      onClick={onToggle}
    >
      <div className="flex items-center space-x-2">
        {icon}
        <span className="font-medium text-gray-800">{title}</span>
        {count !== undefined && (
          <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-medium">{count}</span>
        )}
      </div>
      {isExpanded ? (
        <ChevronDown className="w-4 h-4 text-gray-500" />
      ) : (
        <ChevronRight className="w-4 h-4 text-gray-500" />
      )}
    </button>
    {isExpanded && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="border-t border-gray-100"
      >
        <div className="p-3">{children}</div>
      </motion.div>
    )}
  </div>
);

export default ShipmentRouteDrawer;
